import * as React from 'react';

import {
  CounterpartyGroupSortOption,
  enumCounterpartyGroupSortOption,
  enumOperationType,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  SearchField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash2 } from 'lucide-react';

import { counterpartyGroupBulkDeleteAtom } from '../../_graphql/mutationCounterpartyGroupCrud';
import * as state from '../../_state/counterpartyGroupsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { pricingRoutePaths } from '../../routes';
import { CounterpartyGroupRecord } from '../../types/pricing';

import { CounterpartyGroupFormDialog } from './CounterpartyGroupFormDialog';
import { useCounterpartyGroupColumns } from './hooks/useCounterpartyGroupColumns';

import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  { asc: CounterpartyGroupSortOption; desc: CounterpartyGroupSortOption }
> = {
  name: {
    asc: enumCounterpartyGroupSortOption.NameAsc,
    desc: enumCounterpartyGroupSortOption.NameDesc,
  },
  operationType: {
    asc: enumCounterpartyGroupSortOption.OperationTypeAsc,
    desc: enumCounterpartyGroupSortOption.OperationTypeDesc,
  },
};

export const CounterpartyGroupListView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useModuleLocation();

  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [searchTerm, setSearchTerm] = useDebouncedAtom(state.$searchTerm, 300);
  const [selectedOperationType, setSelectedOperationType] = useAtom(
    state.$selectedOperationType,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteCounterpartyGroups,
  );

  const bulkDelete = useSetAtom(counterpartyGroupBulkDeleteAtom({}));

  const handleRowClick = React.useCallback(
    (group: CounterpartyGroupRecord) => {
      setLocation(
        pricingRoutePaths.counterpartyGroupDetail as '/pricing/groups/:id',
        { params: { id: group.id } },
      );
    },
    [setLocation],
  );
  const handlePaginationChange = React.useCallback(
    ({
      limit: newLimit,
      offset: newOffset,
    }: {
      limit: number;
      offset: number;
    }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumCounterpartyGroupSortOption.NameAsc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );
  const handleCreateClick = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);

  const counterpartyGroups = data?.counterpartyGroups ?? [];
  const displayTotal = data?.totalCount ?? 0;

  const selectedRows = React.useMemo(() => {
    if (selectedKeys === 'all') return counterpartyGroups;
    const keySet = selectedKeys as Set<React.Key>;
    return counterpartyGroups.filter((g) => keySet.has(g.id));
  }, [selectedKeys, counterpartyGroups]);

  const handleBulkDelete = React.useCallback(async () => {
    if (selectedRows.length === 0) return;
    try {
      await bulkDelete({ ids: selectedRows.map((g) => g.id) });
      toast.success(
        t.pricing.counterpartyGroups.toast.bulkDeleted(selectedRows.length),
      );
      setSelectedKeys(new Set());
      refetch?.();
    } catch (err) {
      toast.error(
        t.pricing.counterpartyGroups.toast.deleteError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [selectedRows, bulkDelete, toast, t, setSelectedKeys, refetch]);

  const { columns, columnVisibilityState } = useCounterpartyGroupColumns({
    onRowClick: handleRowClick,
  });

  const emptyPanelProps = {
    title: t.pricing.counterpartyGroups.list.empty.title,
    description: (
      <span>{t.pricing.counterpartyGroups.list.empty.description}</span>
    ),
    action: (
      <Button
        onPress={handleCreateClick}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.pricing.counterpartyGroups.list.createButton}
      </Button>
    ),
    centered: true,
  };

  return (
    <>
      <ListScreenLayout
        filters={
          <div className="flex gap-2">
            <SearchField
              autoFocus
              value={searchTerm}
              onChange={(value) => {
                setSearchTerm(value ?? '');
                setOffset(0);
              }}
              placeholder={t.pricing.counterpartyGroups.list.search}
              aria-label={t.pricing.counterpartyGroups.list.ariaLabel}
              className="w-64"
            />
            <SelectEnum
              enumValue={enumOperationType}
              showAllOption
              allOptionLabel={
                t.pricing.counterpartyGroups.list.allOperationTypes
              }
              value={selectedOperationType || undefined}
              onChange={(value) => {
                setSelectedOperationType(value ?? '');
                setOffset(0);
              }}
              aria-label={
                t.pricing.counterpartyGroups.table.columns.operationType
              }
              className="w-44"
            />
          </div>
        }
        actions={
          <Button
            onPress={handleCreateClick}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.pricing.counterpartyGroups.list.createButton}
          </Button>
        }
        footer={
          displayTotal > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={displayTotal}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
        className="@CounterpartyGroupListView"
      >
        <DataGrid<CounterpartyGroupRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          columnVisibilityState={columnVisibilityState}
          dataSource={counterpartyGroups}
          rowKey="id"
          aria-label={t.pricing.counterpartyGroups.list.ariaLabel}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          className="w-full text-sm"
          fullWidth
          persistColumnOrderKey="brokerage:counterparty-groups-list"
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.common.errors.loading(
                    'counterparty groups',
                    error?.message ?? 'Unknown error',
                  ),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.pricing.counterpartyGroups.toolbar.selected}
          </div>
          <ConfirmPopoverButton
            onConfirm={handleBulkDelete}
            message={t.pricing.counterpartyGroups.toolbar.delete(
              selectedRows.length,
            )}
            confirmLabel={t.common.actions.delete}
            cancelLabel={t.common.actions.cancel}
            renderTrigger={(props) => (
              <Button
                {...props}
                variant="outline"
                icon={<LucideTrash2 />}
                isDisabled={selectedRows.length < 1}
              >
                {t.pricing.counterpartyGroups.toolbar.delete(
                  selectedRows.length,
                )}
              </Button>
            )}
          />
          <Button
            variant="ghost"
            size="small"
            aria-label={t.common.actions.cancel}
            onPress={() => setSelectedKeys(new Set())}
          >
            {t.common.actions.cancel}
          </Button>
        </div>
      </FloatingToolbar>

      <CounterpartyGroupFormDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
        onSuccess={() => refetch?.()}
      />
    </>
  );
};
